import React, { useState, useEffect, useRef, FormEvent } from "react";
import DatePickerInput from "../controls/DatePickerInput";
import InputTextOnly from "../controls/InputTextOnly";
import { useForm } from "../forms/useForm";
import DollarInputText from "../controls/DollarInputText";
import SelectMenu from "../controls/SelectMenu";
import TextArea from "../controls/TextArea";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Divider } from "primereact/divider";

interface ExpenseTypeOption {
  value: number;
  label: string;
}

interface ExpenseFormValues {
  vendor: string;
  date: Date | null;
  total_amount: string;
  vendor_address: string;
  tax_amount: string;
  expense_type: ExpenseTypeOption | null;
  notes: string;
}

interface ExpenseErrors {
  vendor?: string | null;
  total_amount?: string | null;
  tax_amount?: string | null;
  expense_type?: string | null;
  vendor_address?: string | null;
}

interface InitialExpenseData {
  id?: number;
  business_id?: number;
  user_id?: number;
  vendor?: string;
  vendor_address?: string;
  total_amount?: string;
  tax_amount?: string;
  currency?: string | null;
  date?: string;
  receipt_path?: string;
  original_filename?: string;
  mime_type?: string;
  size?: number;
  expense_type?: number;
  notes?: string | null;
  created_at?: string;
  updated_at?: string;
  user_name?: string;
}

interface UpdatePayload {
  vendor?: string;
  vendor_address?: string;
  total_amount?: number;
  tax_amount?: number;
  date?: string;
  expense_type?: number;
}

interface UploadExpenseFormProps {
  visible: boolean;
  onClose: () => void;
  onUploaded?: (formData: FormData) => Promise<{ success: boolean; data?: any }>;
  onUpdate?: (id: number, payload: UpdatePayload) => Promise<{ success: boolean; data?: any }>;
  onManualCreate?: (payload: any) => Promise<{ success: boolean; data?: any }>;
  initialData?: InitialExpenseData | null;
}

export default function UploadExpenseForm({
  visible,
  onClose,
  onUpdate,
  onManualCreate,
  initialData = null,
}: UploadExpenseFormProps) {
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState<boolean>(false);
  const [progress, setProgress] = useState<number>(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const initialFValues: ExpenseFormValues = {
    vendor: "",
    date: null,
    total_amount: "",
    vendor_address: "",
    tax_amount: "",
    expense_type: null,
    notes: "",
  };

  const expenseTypeOptions: ExpenseTypeOption[] = [
    { value: 1, label: "Receipt" },
    // add other types here if needed
  ];

  const validate = (fieldValues: Partial<ExpenseFormValues> = values): boolean => {
    let temp: ExpenseErrors = { ...errors };
    if (fieldValues.vendor !== undefined)
      temp.vendor = fieldValues.vendor ? null : "Merchant is required";
    if (fieldValues.total_amount !== undefined)
      temp.total_amount =
        fieldValues.total_amount && isNaN(Number(fieldValues.total_amount))
          ? "Total must be a number"
          : null;
    if (fieldValues.tax_amount !== undefined)
      temp.tax_amount =
        fieldValues.tax_amount && isNaN(Number(fieldValues.tax_amount))
          ? "Tax must be a number"
          : null;
    setErrors(temp);
    return Object.values(temp).every((x) => x === null);
  };

  const { values, setValues, errors, setErrors, handleInputChange, resetForm } =
    useForm(initialFValues, false, validate);

  // Type assertions for the values and errors from useForm hook
  const typedValues = values as ExpenseFormValues;
  const typedErrors = errors as ExpenseErrors;

  useEffect(() => {
    if (initialData) {
      const mapped: ExpenseFormValues = {
        vendor: initialData.vendor || "",
        date: initialData.date ? new Date(initialData.date) : null,
        total_amount: String(initialData.total_amount || ""),
        vendor_address: initialData.vendor_address || "",
        tax_amount: String(initialData.tax_amount || ""),
        // Map expense_type to option object expected by SelectMenu
        expense_type: (() => {
          const et = initialData.expense_type;
          const found = expenseTypeOptions.find(
            (o) => String(o.value) === String(et)
          );
          return found || null;
        })(),
        notes: initialData.notes || "",
      };
      setValues(mapped);
      if (initialData.receipt_path) {
        setPreviewUrl(initialData.receipt_path);
      }
    } else {
      resetForm();
      setPreviewUrl(null);
      setFile(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData]);

  useEffect(() => {
    if (file) {
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setPreviewUrl(null);
    }
  }, [file]);

  if (!visible) return null;

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    // validate current values
    if (!validate(typedValues)) return;

    // If editing an existing expense, call onUpdate with allowed fields
    if (initialData && initialData.id && onUpdate) {
      const payload: UpdatePayload = {};
      if (typedValues.vendor) payload.vendor = typedValues.vendor;
      if (typedValues.vendor_address) payload.vendor_address = typedValues.vendor_address;
      if (typedValues.total_amount)
        payload.total_amount = Number(typedValues.total_amount);
      if (typedValues.tax_amount) payload.tax_amount = Number(typedValues.tax_amount);
      if (typedValues.date)
        payload.date = typedValues.date instanceof Date
            ? typedValues.date.toISOString().slice(0, 10)
            : typedValues.date;

      if (typedValues.expense_type) {
        payload.expense_type = typeof typedValues.expense_type === "object"
          ? typedValues.expense_type.value
          : typedValues.expense_type;
      }

      setUploading(true);
      try {
        const res = await onUpdate(initialData.id, payload);
        return res;
      } finally {
        setUploading(false);
      }
    }

    // create flow — always use manual-create API
    if (typeof onManualCreate === "function") {
      setUploading(true);
      setProgress(0);
      try {
        // JSON payload
        const payload = {
          vendor: typedValues.vendor || null,
          vendor_address: typedValues.vendor_address || null,
          date: typedValues.date
            ? typedValues.date instanceof Date
              ? typedValues.date.toISOString().slice(0, 10)
              : typedValues.date
            : null,
          expense_type: typedValues.expense_type
            ? typeof typedValues.expense_type === "object"
              ? typedValues.expense_type.value
              : typedValues.expense_type
            : null,
          total_amount: typedValues.total_amount
            ? Number(typedValues.total_amount)
            : null,
          tax_amount: typedValues.tax_amount ? Number(typedValues.tax_amount) : null,
          notes: typedValues.notes || null,
        };
        const res = await onManualCreate(payload);
        if (res && res.success) onClose();
        return res;
      } catch (err) {
        throw err;
      } finally {
        setUploading(false);
        setTimeout(() => setProgress(0), 400);
      }
    }
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold text-gray-900">
          {initialData && initialData.id ? `Edit Expense #${initialData.id}` : "New Expense"}
        </h2>
        <Button
          icon="pi pi-times"
          className="p-button-text p-button-rounded"
          onClick={onClose}
          aria-label="Close"
        />
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Form Section */}
        <div className="lg:col-span-2">
          <Card className="w-full">
            <form onSubmit={submit} className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Vendor Information */}
                <div className="md:col-span-2">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Vendor Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <InputTextOnly
                      label="Merchant Name"
                      name="vendor"
                      onChange={handleInputChange}
                      value={typedValues.vendor}
                      labelOn={true}
                      type="text"
                      error={typedErrors.vendor}
                      className="w-full"
                    />
                    <InputTextOnly
                      label="Vendor Address"
                      name="vendor_address"
                      onChange={handleInputChange}
                      value={typedValues.vendor_address}
                      labelOn={true}
                      type="text"
                      error={typedErrors.vendor_address}
                      className="w-full"
                    />
                  </div>
                </div>

                <Divider />

                {/* Expense Details */}
                <div className="md:col-span-2">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Expense Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div className="w-full">
                      <label className="block text-sm font-medium text-gray-900 mb-2">
                        Date
                      </label>
                      <DatePickerInput
                        name="date"
                        value={typedValues.date}
                        onChange={handleInputChange}
                        parentClass="w-full"
                        labelOn={false}
                        className="w-full"
                        dateFormat="LLL dd, YYY"
                      />
                    </div>
                    <SelectMenu
                      title="Expense Type"
                      name="expense_type"
                      options={expenseTypeOptions}
                      value={typedValues.expense_type}
                      handleInputChange={handleInputChange}
                      error={typedErrors.expense_type}
                      parentClass="w-full"
                    />
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <DollarInputText
                      label="Total Amount"
                      name="total_amount"
                      className="w-full"
                      onChange={handleInputChange}
                      value={typedValues.total_amount}
                      labelOn={true}
                      error={typedErrors.total_amount}
                    />
                    <DollarInputText
                      label="Tax Amount"
                      name="tax_amount"
                      className="w-full"
                      onChange={handleInputChange}
                      value={typedValues.tax_amount}
                      labelOn={true}
                      error={typedErrors.tax_amount}
                    />
                  </div>
                </div>

                {/* Notes */}
                <div className="md:col-span-2">
                  <TextArea
                    label="Notes"
                    name="notes"
                    onChange={handleInputChange}
                    value={typedValues.notes}
                    labelOn={true}
                    rows={3}
                    parentClass="w-full"
                  />
                </div>
              </div>

              {/* Action Buttons */}
              <Divider />
              <div className="flex justify-end gap-3 mt-6">
                <Button
                  label="Cancel"
                  icon="pi pi-times"
                  className="p-button-text"
                  onClick={onClose}
                />
                <Button
                  label={uploading ? "Saving..." : "Save Expense"}
                  icon={uploading ? "pi pi-spin pi-spinner" : "pi pi-check"}
                  loading={uploading}
                  type="submit"
                />
              </div>
            </form>
          </Card>
        </div>

        {/* File Upload Section */}
        <div className="lg:col-span-1">
          <Card title="Receipt Upload" className="w-full h-fit">
            <div className="p-4">
              <div
                className="border-2 border-dashed border-gray-300 rounded-lg h-80 flex flex-col items-center justify-center cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors"
                onDragOver={(e: React.DragEvent<HTMLDivElement>) => e.preventDefault()}
                onDrop={(e: React.DragEvent<HTMLDivElement>) => {
                  e.preventDefault();
                  if (
                    e.dataTransfer &&
                    e.dataTransfer.files &&
                    e.dataTransfer.files[0]
                  ) {
                    setFile(e.dataTransfer.files[0]);
                  }
                }}
                onClick={() => {
                  if (fileInputRef.current)
                    fileInputRef.current.click();
                }}
              >
                {!previewUrl ? (
                  <div className="flex flex-col items-center text-center text-gray-500">
                    <i className="pi pi-upload text-4xl mb-4 text-gray-400"></i>
                    <div className="font-medium text-gray-700 mb-2">
                      Drag & drop receipt here
                    </div>
                    <div className="text-sm text-gray-400">
                      or click to browse files
                    </div>
                    <div className="text-xs text-gray-400 mt-2">
                      Supports: JPG, PNG, PDF
                    </div>
                  </div>
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center">
                    <img
                      src={previewUrl}
                      alt="Receipt preview"
                      className="max-w-full max-h-full object-contain rounded-md shadow-sm"
                    />
                    
                    {progress > 0 && (
                      <div className="w-full mt-4 px-4">
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                        <div className="text-xs text-gray-600 mt-1 text-center">
                          Uploading... {progress}%
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Hidden file input */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,application/pdf"
                  className="hidden"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    if (e.target.files && e.target.files[0])
                      setFile(e.target.files[0]);
                  }}
                />
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
